'use client';

import { IconButton } from '@chakra-ui/react';
import { useCallback, useContext, useState } from 'react';
import { FaHeart, FaRegHeart } from 'react-icons/fa6';
import { WishlistContext } from '@/contexts/WishlistContext';

type WishlistButtonProps = {
  productId: string;
  productOptId?: string | null;
  productOptLabel?: string | null;
  size?: 'sm' | 'md';
  pos?: 'absolute' | 'static';
  top?: string;
  bottom?: string;
  right?: string;
  zIndex?: string;
};

const WishlistButton = ({
  productId,
  productOptId = null,
  productOptLabel = null,
  size = 'sm',
  pos = 'absolute',
  top,
  bottom,
  right = '10px',
  zIndex = '4',
}: WishlistButtonProps) => {
  // "bottom" ganha a "top" quando ambos não são indicados — só um dos dois
  // deve ficar ativo de cada vez.
  const resolvedTop = bottom ? undefined : (top ?? '10px');
  const { isInWishlist, addItem, removeItem, wishlistIsLoading } =
    useContext(WishlistContext);
  const [isSaving, setIsSaving] = useState(false);

  const saved = isInWishlist(productId, productOptId);
  const boxSize = size === 'sm' ? '32px' : '38px';
  const iconSize = size === 'sm' ? '14px' : '16px';

  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (isSaving || wishlistIsLoading) return;

      setIsSaving(true);
      if (saved) {
        await removeItem(productId, productOptId);
      } else {
        await addItem(productId, productOptId, productOptLabel);
      }
      setIsSaving(false);
    },
    [
      saved,
      addItem,
      removeItem,
      productId,
      productOptId,
      productOptLabel,
      isSaving,
      wishlistIsLoading,
    ],
  );

  return (
    <IconButton
      type="button"
      onClick={handleClick}
      pos={pos}
      top={pos === 'absolute' ? resolvedTop : undefined}
      bottom={pos === 'absolute' ? bottom : undefined}
      right={pos === 'absolute' ? right : undefined}
      zIndex={zIndex}
      w={boxSize}
      h={boxSize}
      minW={boxSize}
      borderRadius="full"
      bg="rgba(10,27,43,0.55)"
      backdropFilter="blur(8px)"
      transition="all 0.2s"
      _hover={{ bg: 'rgba(10,27,43,0.75)', transform: 'scale(1.08)' }}
      aria-label={saved ? 'Remover dos favoritos' : 'Guardar nos favoritos'}
      title={saved ? 'Remover dos favoritos' : 'Guardar nos favoritos'}
    >
      {saved ? (
        <FaHeart size={iconSize} color="#FF4D6D" />
      ) : (
        <FaRegHeart size={iconSize} color="#FFFFFF" />
      )}
    </IconButton>
  );
};

export default WishlistButton;
